import { IonAccordion } from '@ionic/react';
import { IonicButton } from 'ionic-ui';
import React, { ComponentProps, useMemo } from 'react';
import { Controller, FormProvider } from 'react-hook-form';
import { useSelector } from 'react-redux';
import AccordionHeader from '../../../../../../components/Dashboard/getting-started/accordion/AccordionHeader';
import SetupItem from '../../../../../../components/Dashboard/getting-started/accordion/SetupItem';
import FileDropBox from '../../../../../../components/ui/FileDropBox/FileDropBox';
import DocumentStatusField, {
  UploadedDoc,
} from '../../../../../../components/Dashboard/getting-started/DocumentStatusField';
import DocStatusTag from '../../../../../../components/Dashboard/settings/ui/DocStatusTag';
import usePersonalKycService from '../../../../../../services/userProfile/hooks/usePersonalKycService';
import { RootState } from '../../../../../../store/store';

type Props = ComponentProps<typeof IonAccordion>;

const acceptedDocTypes = {
  'image/jpeg': ['.jpeg', '.jpg'] as const,
  'image/png': ['.png'] as const,
  'application/pdf': ['.pdf'] as const,
};

export default function PersonalKycAccordion(props: Props) {
  const { methods, onSave } = usePersonalKycService();
  const {
    control,
    formState: { isDirty },
  } = methods;

  const userKyc = useSelector(
    (state: RootState) => state.userProfile?.user_kyc_documents
  );

  const docMap = useMemo<Record<string, UploadedDoc>>(() => {
    const map: Record<string, UploadedDoc> = {};
    for (const d of userKyc || []) {
      if (!d?.name) continue;
      const key = d.name.toUpperCase();
      // Prefer approved when duplicates exist.
      if (map[key]?.verification_status === 'approved') continue;
      map[key] = d as UploadedDoc;
    }
    return map;
  }, [userKyc]);

  const bvnDoc = docMap['BVN'];
  const bvnLocked =
    bvnDoc?.verification_status === 'approved' ||
    bvnDoc?.verification_status === 'pending';

  return (
    <IonAccordion value="personal-kyc" {...props}>
      <AccordionHeader>Personal Verification</AccordionHeader>
      <FormProvider {...methods}>
        <div
          slot="content"
          className="flex flex-col p-4 gap-4 bg-background dark:bg-background-accent"
        >
          {/* NIN */}
          <DocumentStatusField
            label="National Identification (NIN)"
            required
            doc={docMap['NIN']}
          >
            <Controller
              control={control}
              name="NIN_NUMBER"
              render={({ field, fieldState }) => (
                <SetupItem
                  {...field}
                  label="NIN number*"
                  fieldType="text"
                  placeholder="11-digit NIN"
                  hint={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="NIN_FILE"
              render={({ field, fieldState }) => (
                <FileDropBox
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  maxFiles={1}
                  allowCropping={false}
                  allowPreview
                  acceptedTypes={acceptedDocTypes as any}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
          </DocumentStatusField>

          {/* BVN — text only, no file */}
          <div className="border border-border rounded-md p-4 space-y-3 bg-background dark:bg-background-accent">
            <div className="flex justify-between items-center gap-2">
              <p className="font-medium high-emphasis-text">
                Bank Verification Number (BVN)
                <span className="text-red-500"> *</span>
              </p>
              {bvnDoc && <DocStatusTag status={bvnDoc.verification_status} />}
            </div>
            {bvnLocked ? (
              <p className="text-xs medium-emphasis-text">
                {bvnDoc?.verification_status === 'approved'
                  ? 'BVN verified. No changes allowed.'
                  : 'BVN submitted and is under review.'}
              </p>
            ) : (
              <>
                {bvnDoc?.rejection_reason && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-xs font-semibold text-red-600">
                      Reason for rejection:
                    </p>
                    <p className="text-xs text-red-600 mt-1">
                      {bvnDoc.rejection_reason}
                    </p>
                  </div>
                )}
                <Controller
                  control={control}
                  name="BVN_NUMBER"
                  render={({ field, fieldState }) => (
                    <SetupItem
                      {...field}
                      label="BVN number*"
                      fieldType="text"
                      placeholder="11-digit BVN"
                      hint={fieldState.error?.message}
                    />
                  )}
                />
              </>
            )}
          </div>

          {/* UTILITY_BILL */}
          <DocumentStatusField
            label="Utility Bill (proof of residential address)"
            required
            doc={docMap['UTILITY_BILL']}
          >
            <Controller
              control={control}
              name="UTILITY_BILL_FILE"
              render={({ field, fieldState }) => (
                <FileDropBox
                  {...field}
                  value={field.value}
                  onChange={field.onChange}
                  maxFiles={1}
                  allowCropping={false}
                  allowPreview
                  acceptedTypes={acceptedDocTypes as any}
                  errorMessage={fieldState.error?.message}
                />
              )}
            />
            <Controller
              control={control}
              name="UTILITY_BILL_ADDRESS"
              render={({ field, fieldState }) => (
                <SetupItem
                  {...field}
                  label="Address on utility bill"
                  fieldType="text"
                  placeholder="As it appears on the bill"
                  hint={fieldState.error?.message}
                />
              )}
            />
          </DocumentStatusField>

          <div className="pt-2">
            <IonicButton
              type="button"
              onClick={onSave}
              disabled={!isDirty}
              className="w-full rounded-sm"
            >
              Save
            </IonicButton>
          </div>
        </div>
      </FormProvider>
    </IonAccordion>
  );
}
